<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import { 
  Globe, 
  Search, 
  Building2,
  Settings2,
  LayoutGrid
} from 'lucide-vue-next'

import RegionSelector from '@/components/user/userprofile/RegionSelector.vue'
import CountSelector from '@/components/user/userprofile/CountSelector.vue'
import BookieList from '@/components/user/userprofile/BookieList.vue'
import { useProfilesStore } from '@/store/modules/userProfile'
import type { Region } from '@/types/user'
import { useStore } from 'vuex'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type TriStateValue = 0 | 1 | 2
type VisibilityFilter = 'all' | 'included' | 'excluded' | 'auto'

const store = useStore()
const userProfilesStore = useProfilesStore()
const toast = useToast()
import { getInferredCurrency } from '@/utils/conductor.util'

const displayCurrency = computed(() => userProfilesStore.profile.preferredCurrency || getInferredCurrency())

const regions = ref<Region[]>([])
const isLoading = ref(true)

const localCount = ref<number>(0)
const localIncluded = ref<string[]>([])
const localExcluded = ref<string[]>([])
const localRegions = ref<string[]>([])

const isDirty = ref(false)
let skipAutosave = false
let updatingStore = false
let saveTimeout: ReturnType<typeof setTimeout> | null = null
const isSaving = ref(false)
const manualChange = ref(false)

// Search and Filter State
const searchQuery = ref('')
const visibilityFilter = ref<VisibilityFilter>('all')

function triggerAutosave() {
  if (saveTimeout) clearTimeout(saveTimeout)
  saveTimeout = setTimeout(() => saveProfile(), 2000)
}

watch(
  () => userProfilesStore.profile,
  (newProfile) => {
    skipAutosave = true
    localCount.value = Number(newProfile.displayCount) || 0

    const bookieNameToId: Record<string, string> = {}
    regions.value.forEach(region => {
      region.bookies.forEach(bookie => {
        bookieNameToId[bookie.name] = bookie.id
      })
    })

    localIncluded.value = (newProfile.included || []).map(item =>
      bookieNameToId[item] || item
    )
    localExcluded.value = (newProfile.excluded || []).map(item =>
      bookieNameToId[item] || item
    )
    localRegions.value = [...(newProfile.regions || [])]

    skipAutosave = false
  },
  { deep: true, immediate: true }
)

watch([localCount, localRegions, localIncluded, localExcluded], () => {
  if (!skipAutosave && !updatingStore && manualChange.value) {
    isDirty.value = true
    triggerAutosave()
    manualChange.value = false
  }
}, { deep: true })

const uniqueBookies = computed(() => {
  const bookieMap: Record<string, Region['bookies'][0] & { icon: string }> = {}
  const selectedRegions = regions.value.filter(r => localRegions.value.includes(r.id))
  const sourceRegions = selectedRegions.length
    ? selectedRegions
    : regions.value.find(r => r.id === 'world')
      ? [regions.value.find(r => r.id === 'world')!]
      : []

  sourceRegions.forEach(region => {
    region.bookies.forEach(bookie => {
      const label = bookie.id
      bookieMap[bookie.id] = { ...bookie, icon: `${label}` }
    })
  })

  return Object.values(bookieMap).sort((a, b) => a.name.localeCompare(b.name))
})

const filteredBookies = computed(() => {
  let list = uniqueBookies.value

  // Apply search
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    list = list.filter(b => b.name.toLowerCase().includes(q))
  }

  // Apply visibility filter
  if (visibilityFilter.value !== 'all') {
    list = list.filter(b => {
      if (visibilityFilter.value === 'included') return localIncluded.value.includes(b.id)
      if (visibilityFilter.value === 'excluded') return localExcluded.value.includes(b.id)
      if (visibilityFilter.value === 'auto') return !localIncluded.value.includes(b.id) && !localExcluded.value.includes(b.id)
      return true
    })
  }

  return list
})

const bookieSliderValue = computed<Record<string, TriStateValue>>(() => {
  const map: Record<string, TriStateValue> = {}
  uniqueBookies.value.forEach(bookie => {
    if (localIncluded.value.includes(bookie.id)) map[bookie.id] = 2
    else if (localExcluded.value.includes(bookie.id)) map[bookie.id] = 0
    else map[bookie.id] = 1
  })
  return map
})

function onBookieChange(bookieId: string, newValue: TriStateValue) {
  const inc: string[] = []
  const exc: string[] = []

  uniqueBookies.value.forEach(bookie => {
    if (bookie.id === bookieId) {
      if (newValue === 2) inc.push(bookie.id)
      else if (newValue === 0) exc.push(bookie.id)
    } else {
      if (localIncluded.value.includes(bookie.id)) inc.push(bookie.id)
      else if (localExcluded.value.includes(bookie.id)) exc.push(bookie.id)
    }
  })

  localIncluded.value = inc
  localExcluded.value = exc
  manualChange.value = true
}

function onRegionChange(newRegionIds: string[]) {
  localRegions.value = [...newRegionIds]
  manualChange.value = true
}

function onCountChange(newCount: number | null) {
  localCount.value = newCount ?? 0
  manualChange.value = true
}

function onCurrencyChange(currency: 'EUR' | 'GBP' | 'USD') {
  userProfilesStore.profile.preferredCurrency = currency
  manualChange.value = true
}

async function saveProfile() {
  if (saveTimeout) {
    clearTimeout(saveTimeout)
    saveTimeout = null
  }
  if (isSaving.value) return
  isSaving.value = true
  updatingStore = true
  try {
    userProfilesStore.profile.displayCount = localCount.value
    userProfilesStore.profile.included = [...localIncluded.value]
    userProfilesStore.profile.excluded = [...localExcluded.value]
    userProfilesStore.profile.regions = [...localRegions.value]

    if (userProfilesStore.profile.userId) {
      await userProfilesStore.saveToDB(userProfilesStore.profile.userId)
      toast.add({ severity: 'success', summary: 'Preferences saved', life: 5000 })
    } else {
      userProfilesStore.saveLocal()
      toast.add({ severity: 'success', summary: 'Saved locally', life: 5000 })
    }

    isDirty.value = false
  } catch (e: any) {
    toast.add({ severity: 'error', summary: 'Save failed', detail: e.message || '', life: 5000 })
  } finally {
    updatingStore = false
    isSaving.value = false
  }
}

async function loadProfile(userId?: number) {
  isLoading.value = true
  try {
    if (userId) {
      await userProfilesStore.loadFromDB()
    } else {
      userProfilesStore.resetProfile()
      userProfilesStore.loadLocal()
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  regions.value = store.getters['oddsModule/regions']
  if (userProfilesStore.profile.userId) {
    await loadProfile(userProfilesStore.profile.userId)
  } else {
    userProfilesStore.loadLocal()
    isLoading.value = false
  }
})

defineExpose({
  save: saveProfile
})
</script>

<template>
  <div v-if="isLoading" class="flex items-center justify-center p-20">
    <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
  </div>
  
  <div v-else class="flex flex-col h-full min-h-0 gap-6">
    <!-- Bookmaker Controls Section -->
    <section class="flex flex-col flex-1 min-h-0">
      <div class="mb-5 flex-shrink-0">
        <div class="flex items-center gap-2">
          <Settings2 class="w-5 h-5 text-gray-400" />
          <h3 class="text-lg font-bold text-gray-900 dark:text-white">Odds Preferences</h3>
        </div>
        <p class="text-sm text-gray-500 mt-0.5 ml-7">Fine-tune which bookmakers appear in your results</p>
      </div>

      <div class="mt-4 flex flex-col flex-1 min-h-0 overflow-hidden rounded-xl border border-border dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
        <!-- Configuration Controls -->
        <div class="flex-shrink-0 p-4 border-b border-gray-50 dark:border-gray-800 bg-gray-50/20 dark:bg-gray-800/10 space-y-4">
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
            <div class="flex flex-col min-w-0">
              <div class="flex items-center gap-2 mb-2">
                <Globe class="w-3.5 h-3.5 text-gray-400" />
                <span class="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Active Regions</span>
              </div>
              <RegionSelector
                v-model="localRegions"
                :regions="regions.filter(r => r.id !== 'world')"
                @update:modelValue="onRegionChange"
              />
            </div>

            <div class="flex flex-col min-w-0">
              <div class="flex items-center gap-2 mb-2">
                <LayoutGrid class="w-3.5 h-3.5 text-gray-400" />
                <span class="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Auto Results Limit</span>
              </div>
              <CountSelector v-model="localCount" @change="onCountChange" />
            </div>

            <div class="flex flex-col min-w-0 col-span-2 md:col-span-1">
              <div class="flex items-center gap-2 mb-2">
                <span class="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Preferred Currency</span>
              </div>
              <Select :modelValue="displayCurrency" @update:modelValue="onCurrencyChange">
                <SelectTrigger class="w-full h-9 rounded-xl border-border dark:border-gray-800 bg-white dark:bg-gray-950 focus:ring-primary text-[10px]">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent class="rounded-xl border-border dark:border-gray-800">
                  <SelectItem value="EUR" class="text-xs">Euro (EUR)</SelectItem>
                  <SelectItem value="GBP" class="text-xs">Sterling (GBP)</SelectItem>
                  <SelectItem value="USD" class="text-xs">Dollar (USD)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <!-- Filter Controls -->
          <div class="flex items-center justify-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
            <Button 
              v-for="filter in (['all', 'included', 'excluded', 'auto'] as const)" 
              :key="filter"
              variant="ghost" 
              size="sm"
              @click="visibilityFilter = filter"
              :class="[
                'text-[10px] h-7 px-3 rounded-xl font-bold uppercase tracking-tight transition-all whitespace-nowrap',
                visibilityFilter === filter 
                  ? 'bg-primary/10 text-primary shadow-sm' 
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5'
              ]"
            >
              {{ filter }}
            </Button>
          </div>
        </div>

        <!-- Search Header -->
        <div class="flex-shrink-0 p-2 md:p-4 border-b border-gray-50 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-800/20 relative group">
          <Search class="absolute left-7 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none transition-colors group-focus-within:text-primary" />
          <Input 
            v-model="searchQuery"
            placeholder="Search bookmakers..."
            class="pl-10 h-10 border-border dark:border-gray-700 bg-white dark:bg-gray-800 focus-visible:ring-primary rounded-xl text-sm"
          />
        </div>

        <!-- Scrollable List Area -->
        <div class="flex-1 overflow-y-auto custom-scrollbar">
          <div v-if="filteredBookies.length">
            <BookieList 
              :bookies="filteredBookies" 
              :sliderValues="bookieSliderValue" 
              @change="onBookieChange" 
            />
          </div>
          
          <div v-else class="py-20 flex flex-col items-center justify-center text-gray-400 text-center px-6">
            <div class="p-4 rounded-full bg-gray-50 dark:bg-gray-800 mb-4">
              <Building2 class="w-8 h-8 opacity-20" />
            </div>
            <p class="text-sm font-medium">No bookmakers found</p>
            <p class="text-xs mt-1">Try adjusting your search or filters</p>
            <Button variant="ghost" size="sm" class="mt-4 text-primary font-bold" @click="searchQuery = ''; visibilityFilter = 'all'">
              Clear All Filters
            </Button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0,0,0,0.05);
  border-radius: 10px;
}
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.05);
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(0,0,0,0.1);
}
</style>